import { useEffect, useRef, useState } from "react";
import { Copy, Download, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const esc = (s) => String(s).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
const sheet = (title, body, fp) => {
  const html = `<!doctype html><meta charset="utf-8"><title>${esc(title)}</title>
<style>body{font-family:Georgia,serif;max-width:640px;margin:48px auto;color:#111}code{display:block;font:15px/1.6 monospace;word-break:break-all;border:2px solid #111;padding:16px;margin:16px 0}small{color:#555}</style>
<h1>${esc(title)}</h1><p><b>Site:</b> Webber Dynasty Photos (${esc(window.location.host)})<br><b>Date:</b> ${new Date().toLocaleString()}<br><b>Key fingerprint:</b> ${esc(fp)}</p>
<code>${esc(body)}</code>
<p><b>Keep offline.</b> Needed to recover private photos if the server key is lost.</p>
<p>To restore: Settings → Storage → Advanced → Recovery key → Restore, or paste the key as <b>PRIVATE_ENC_KEY</b> in the deployment Secrets.</p>
<small>This key only recovers the server key. It does not let anyone view family members' private photos on the site.</small>
<script>window.print()</script>`;
  const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(new Blob([html], { type: "text/html" })), download: `${title.replace(/\W+/g, "-").toLowerCase()}.html` });
  a.click();
};

const Unlock = ({ info, onUnlock }) => {
  const [pw, setPw] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { onUnlock((await api.post("/admin/recovery/unlock", info.has_password ? { password: pw } : { code: pw })).data.unlock); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="flex flex-wrap items-center gap-2 mt-3">
      {!info.has_password && !sent ? (
        <button className="btn-outline" onClick={() => api.post("/admin/recovery/email-code").then(() => setSent(true)).catch((e) => toast.error(errMsg(e)))} data-testid="recovery-email-code">Email me a code</button>
      ) : (
        <>
          <input type={info.has_password ? "password" : "text"} className="input max-w-xs" placeholder={info.has_password ? "Your password" : "6-digit code"} value={pw}
            onChange={(e) => setPw(e.target.value)} onKeyDown={(e) => e.key === "Enter" && go()} data-testid="recovery-password-input" />
          <button className="btn-primary" disabled={!pw || busy} onClick={go} data-testid="recovery-unlock-button">{busy ? <Loader2 size={14} className="animate-spin" /> : "Confirm"}</button>
        </>
      )}
    </div>
  );
};

const Pieces = ({ unlock, info }) => {
  const [labels, setLabels] = useState(["", "", ""]);
  const [made, setMade] = useState(null);
  const make = async () => {
    if (info.pieces && !window.confirm("Create a new set? The old pieces will stop working.")) return;
    try { setMade((await api.post("/admin/recovery/pieces", { unlock, labels })).data); window.dispatchEvent(new Event("key-backup-changed")); } catch (e) { toast.error(errMsg(e)); }
  };
  if (made) return (
    <div className="mt-3 space-y-2" data-testid="recovery-pieces-made">
      <p className="text-sm text-amber-700">Shown only once. Download each sheet and give it to its holder. Any 2 pieces rebuild the key.</p>
      {made.pieces.map((p) => (
        <div key={p.n} className="flex items-center gap-2 text-sm">
          <b>Piece {p.n} of 3</b> <span className="text-neutral-500">{p.label || "no label"}</span>
          <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => {
            sheet(`Piece ${p.n} of 3 (any 2 rebuild the key)${p.label ? `, held by ${p.label}` : ""}`, p.piece, made.fingerprint);
            api.post("/admin/recovery/piece-downloaded", { unlock, set_id: made.set_id, n: p.n }).then(() => window.dispatchEvent(new Event("key-backup-changed"))).catch(() => {});
          }} data-testid={`recovery-piece-download-${p.n}`}><Download size={12} /> Sheet</button>
        </div>
      ))}
    </div>
  );
  return (
    <div className="mt-3 space-y-2">
      {info.pieces && <p className="text-sm text-neutral-500" data-testid="recovery-pieces-meta">Current set {info.pieces.set_id} from {new Date(info.pieces.created_at).toLocaleDateString()}: {info.pieces.labels.map((l) => `#${l.n} ${l.label || "?"}`).join(", ")}</p>}
      <div className="flex flex-wrap gap-2">{labels.map((l, i) => (
        <input key={i} className="input max-w-[12rem]" placeholder={`Piece ${i + 1} holder`} value={l} onChange={(e) => setLabels(labels.map((x, k) => (k === i ? e.target.value : x)))} data-testid={`recovery-piece-label-${i + 1}`} />
      ))}</div>
      <button className="btn-outline" onClick={make} data-testid="recovery-create-pieces">Create key pieces</button>
    </div>
  );
};

const Rebuild = ({ unlock }) => {
  const [p, setP] = useState(["", ""]);
  const [out, setOut] = useState(null);
  const go = async () => { try { setOut((await api.post("/admin/recovery/rebuild", { unlock, pieces: p })).data); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="mt-3 space-y-2">
      {p.map((v, i) => <textarea key={i} className="input w-full font-mono text-xs" rows={2} placeholder={`Piece ${i + 1}`} value={v} onChange={(e) => setP(p.map((x, k) => (k === i ? e.target.value : x)))} data-testid={`recovery-rebuild-piece-${i + 1}`} />)}
      <button className="btn-outline" disabled={!p[0] || !p[1]} onClick={go} data-testid="recovery-rebuild-button">Rebuild key</button>
      {out && <p className="text-sm" data-testid="recovery-rebuilt">Key (fingerprint {out.fingerprint}{out.matches_server ? ", matches this server" : ""}): <code className="break-all">{out.key}</code><br />Paste it as PRIVATE_ENC_KEY in the deployment Secrets, then republish.</p>}
    </div>
  );
};

// Settings -> Storage -> Advanced -> Recovery key (admin only). Every action is logged server-side.
export const RecoveryKeyCard = () => {
  const [info, setInfo] = useState(null);
  const [unlock, setUnlock] = useState(null);
  const [key, setKey] = useState(null);
  const [show, setShow] = useState(false);
  const [log, setLog] = useState([]);
  const [backup, setBackup] = useState(null);
  const ref = useRef(null);
  useEffect(() => { api.get("/admin/recovery").then(({ data }) => setInfo(data)).catch(() => {}); }, []);
  useEffect(() => { if (info && window.location.hash === "#recovery-key") ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [info]);
  useEffect(() => {
    if (!unlock) return undefined;
    const load = () => api.get("/admin/recovery/backup-status").then(({ data }) => setBackup(data)).catch(() => {});
    load();
    window.addEventListener("key-backup-changed", load);
    return () => window.removeEventListener("key-backup-changed", load);
  }, [unlock]);
  useEffect(() => { if (unlock) api.get("/admin/recovery/log").then(({ data }) => setLog(data.log)); }, [unlock, key]);
  const fetchKey = async (action) => {
    try {
      const { data } = await api.post("/admin/recovery/key", { unlock, action });
      setKey(data.key);
      if (action === "copy") { await navigator.clipboard.writeText(data.key); toast.success("Key copied"); }
      if (action === "download") { sheet("Recovery key", data.key, data.fingerprint); window.dispatchEvent(new Event("key-backup-changed")); }
      if (action === "reveal") setShow(true);
    } catch (e) { toast.error(errMsg(e)); if (e?.response?.status === 401) setUnlock(null); }
  };
  if (!info) return null;
  return (
    <section className="settings-card" ref={ref} id="recovery-key" data-testid="recovery-key-card">
      <h2 className="text-base font-semibold text-neutral-900 flex items-center gap-2"><KeyRound size={16} /> Recovery key</h2>
      <p className="text-sm text-neutral-500 mt-1">Recovers the server's encryption key if it's ever lost. It never gives access to anyone's Private photos on the site. Fingerprint: <b data-testid="recovery-fingerprint">{info.fingerprint || "key missing"}</b></p>
      {!unlock ? <Unlock info={info} onUnlock={setUnlock} /> : (
        <>
          {backup && (
            <p className={`text-sm font-semibold mt-3 ${backup.done ? "text-emerald-700" : "text-amber-700"}`} data-testid="recovery-backup-status">
              {!backup.sheet && !backup.pieces ? "Not backed up yet" : `Recovery sheet ${backup.sheet ? "✓" : "–"} · Key pieces ${backup.pieces} of 3`}
            </p>
          )}
          {info.key_present && (
            <div className="flex flex-wrap items-center gap-2 mt-3">
              <code className="text-xs bg-neutral-100 px-2 py-1 rounded break-all" data-testid="recovery-key-value">{key && show ? key : "••••••••••••••••••••••••••••••••••••"}</code>
              <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => (show ? setShow(false) : fetchKey("reveal"))} data-testid="recovery-reveal">{show ? <EyeOff size={12} /> : <Eye size={12} />} {show ? "Hide" : "Reveal"}</button>
              <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => fetchKey("copy")} data-testid="recovery-copy"><Copy size={12} /> Copy</button>
              <button className="btn-outline !py-1 !px-3 text-xs" onClick={() => fetchKey("download")} data-testid="recovery-download"><Download size={12} /> Download recovery sheet</button>
            </div>
          )}
          {info.key_present && <Pieces unlock={unlock} info={info} />}
          <h3 className="text-sm font-semibold mt-4">Restore (rebuild key from 2 pieces)</h3>
          <Rebuild unlock={unlock} />
          <h3 className="text-sm font-semibold mt-4">Access log</h3>
          <ul className="text-xs text-neutral-600 mt-1 max-h-40 overflow-auto" data-testid="recovery-log">
            {log.map((r, i) => <li key={i}>{new Date(r.at).toLocaleString()} · {r.email} · {r.action} · {r.ip}</li>)}
          </ul>
        </>
      )}
    </section>
  );
};
